import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { Search, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";

// Reusable toolbar that sits between a page header and its content (e.g. the
// Call Logs table). Presentational only — it lays out a `left` region (filter
// triggers) and a `right` region (search, view controls) with consistent
// spacing and wrapping. Compose it with `ActionBarButton` / `ActionBarSearch`
// or any nodes. Uses semantic design-system tokens so it is dark-mode safe.
export interface ActionBarProps {
  left?: React.ReactNode;
  right?: React.ReactNode;
  className?: string;
  // How many filters are applied right now. Count each one the user would
  // otherwise remove by hand: the date range is 1, the agent selection is 1
  // however many agents it holds, and every custom condition is 1 each.
  appliedFilterCount?: number;
  // Resets every filter at once. Required for "Clear all" to appear.
  onClearFilters?: () => void;
  clearFiltersLabel?: string;
}

// "Clear all" shows only from the second filter onwards. With one applied, that
// control's own × is the shorter path; past that, removing them one at a time is
// the tedium this exists to cut.
const CLEAR_ALL_MIN_FILTERS = 2;

export function ActionBar({
  left,
  right,
  className,
  appliedFilterCount = 0,
  onClearFilters,
  clearFiltersLabel = "Clear all",
}: ActionBarProps): React.ReactElement {
  const showClearAll =
    !!onClearFilters && appliedFilterCount >= CLEAR_ALL_MIN_FILTERS;

  return (
    <div
      className={cn(
        // 8px top/bottom is the bar's own padding; the horizontal inset comes
        // from the page container so the bar stays aligned with the page title
        // and the table columns below it.
        "flex flex-wrap items-center justify-between gap-2 py-2",
        className
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        {left}
        {showClearAll && (
          <button
            type="button"
            onClick={onClearFilters}
            className="text-destructive hover:text-destructive/80 cursor-pointer whitespace-nowrap px-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 rounded-sm"
          >
            {clearFiltersLabel}
          </button>
        )}
      </div>
      {right && (
        <div className="flex flex-wrap items-center gap-2">{right}</div>
      )}
    </div>
  );
}

// Pill trigger used inside an `ActionBar` (e.g. "Date range", "All agents").
// Pass `asChild` to use it as a Popover/DropdownMenu trigger. Set `active` when
// the control has a value applied so it reads as selected.
export interface ActionBarButtonProps extends React.ComponentProps<"button"> {
  asChild?: boolean;
  active?: boolean;
}

// Shared pill-trigger class. Exported so non-button triggers (e.g. the
// `AgentSelect` combobox used as the "All agents" pill) can render an identical
// control — same size, hover, and focus — instead of drifting.
// Icon sizing, kept separate because it targets DIRECT children. A pill that
// moves its box to a wrapper — so a clear control can sit beside the trigger
// rather than inside it — has to put this on the element the icons actually sit
// in, or the rule silently matches nothing.
export const actionBarIconClassName =
  "[&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-muted-foreground";

// eslint-disable-next-line react-refresh/only-export-components
export const actionBarButtonClassName = cn(
  // 32px tall (h-8), 12px horizontal padding (px-3), 8px icon↔label gap (gap-2)
  // — matches the design spec.
  "inline-flex h-8 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-3 text-sm font-medium text-foreground transition-colors",
  "hover:bg-accent hover:text-accent-foreground",
  "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
  "disabled:pointer-events-none disabled:opacity-50",
  actionBarIconClassName
);

// The applied treatment for a pill: filled, and **no border**. A row of applied
// filters each carrying its own outline reads as dense and busy; the fill already
// says "applied", so the stroke is redundant there. Unapplied pills keep theirs.
//
// Exported because `AgentSelect` renders its own trigger — it replaces the pill
// classes wholesale, so without this it stayed white while its neighbours filled.
export const actionBarButtonActiveClassName =
  "border-transparent bg-accent text-accent-foreground hover:bg-accent";

export const ActionBarButton = React.forwardRef<
  HTMLButtonElement,
  ActionBarButtonProps
>(({ className, asChild = false, active = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      ref={ref}
      data-slot="action-bar-button"
      data-active={active || undefined}
      className={cn(
        actionBarButtonClassName,
        active && actionBarButtonActiveClassName,
        className
      )}
      {...props}
    />
  );
});
ActionBarButton.displayName = "ActionBarButton";

// The × on an applied filter pill.
//
// Every filter control in the bar follows one contract: once applied it takes the
// active treatment, shows what is applied — a **count** for a set of values, the
// value itself for a date range — and offers this × to remove it. `ActionBar`'s
// "Clear all" then appears from the second applied filter onwards.
//
// It renders as a *sibling* of the trigger, inside the pill's non-interactive
// wrapper — a focusable control inside a <button> is interactive content in a
// button, which the HTML content model forbids. Both events still have to stop
// here: Radix opens on pointerdown, and a click that bubbles to the trigger
// reopens the control the user just reset. Getting that wrong is easy, which is
// why this is one component rather than a pattern each control re-derives.
export interface ActionBarPillClearProps {
  onClear: () => void;
  // Accessible name, e.g. "Clear agents".
  label: string;
  className?: string;
}

export function ActionBarPillClear({
  onClear,
  label,
  className,
}: ActionBarPillClearProps): React.ReactElement {
  return (
    <button
      type="button"
      aria-label={label}
      className={cn(
        "text-muted-foreground hover:text-foreground -mr-1 ml-1 inline-flex shrink-0 cursor-pointer items-center rounded-sm",
        className
      )}
      onPointerDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onClick={(event) => {
        event.stopPropagation();
        onClear();
      }}
    >
      <X className="size-3.5" />
    </button>
  );
}

// Search input for an `ActionBar`'s right region. Controlled; render a
// debounced value from the parent for performance on server-backed tables.
export interface ActionBarSearchProps extends Omit<
  React.ComponentProps<"input">,
  "onChange" | "value"
> {
  value: string;
  onValueChange: (value: string) => void;
  containerClassName?: string;
  // Renders a clear button once there is a value.
  onClear?: () => void;
  clearLabel?: string;
  // Pulses the icon while a debounced/in-flight search settles.
  pending?: boolean;
}

export function ActionBarSearch({
  value,
  onValueChange,
  placeholder = "Search…",
  className,
  containerClassName,
  onClear,
  clearLabel = "Clear search",
  pending = false,
  ...props
}: ActionBarSearchProps): React.ReactElement {
  const showClear = onClear !== undefined && value !== "";

  return (
    <div className={cn("relative", containerClassName)}>
      <Search
        className={cn(
          "pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground",
          pending && "animate-pulse"
        )}
      />
      <Input
        // With an `onClear` button the type is always "text": the native
        // "search" clear affordance would sit on top of our button, and flipping
        // the type as the value changes makes browsers rebuild the control
        // (caret loss, IME breakage). Without one, "search" keeps the browser's
        // own clear affordance.
        type={onClear !== undefined ? "text" : "search"}
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        placeholder={placeholder}
        className={cn("h-8 w-56 pl-8", showClear && "pr-8", className)}
        {...props}
      />
      {showClear && (
        <button
          type="button"
          onClick={onClear}
          aria-label={clearLabel}
          className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2 cursor-pointer"
        >
          <X className="size-4" />
        </button>
      )}
    </div>
  );
}
